import BaseController from "./base_controller"
import { toDateInputValue } from "@/utils/dates"

export default class extends BaseController {
  static targets = ["dosesInput", "dateInput", "startDate", "endDate"]
  
  declare readonly dosesInputTarget: HTMLElement
  declare readonly dateInputTarget: HTMLElement
  declare readonly startDateTarget: HTMLInputElement
  declare readonly endDateTarget: HTMLInputElement
  declare readonly hasStartDateTarget: boolean
  declare readonly hasEndDateTarget: boolean

  private suggestedEndDate: string = ''
  
  connect(): void {
    super.connect()
    this.updateEndType(this.isDateModeSelected())
    this.debug("Onboarding form controller connected")
  }
  
  substanceSelected(event: Event): void {
    const target = event.target as HTMLInputElement | null
    if (!target) return
    
    this.debug("Substance selected:", target.value)
    // substance-dose controller handles auto-fill
  }
  
  selectProtocol(event: Event): void {
    const currentTarget = event.currentTarget as HTMLElement | null
    if (!currentTarget) return
    
    const protocolId = currentTarget.dataset.protocolId
    const select = this.element.querySelector<HTMLSelectElement>('[name="user[current_protocol_id]"]')
    if (!select || !protocolId) return
    
    select.value = protocolId
    
    // Visual feedback
    this.element.querySelectorAll('[data-action*="selectProtocol"]').forEach(card => {
      card.classList.remove('bg-primary', 'text-primary-content')
    })
    currentTarget.classList.add('bg-primary', 'text-primary-content')
  }
  
  endTypeChanged(event: Event): void {
    const target = event.target as HTMLInputElement | null
    if (!target) return
    
    this.updateEndType(target.value === 'date')
  }

  private updateEndType(dateMode: boolean): void {
    this.dosesInputTarget.classList.toggle('hidden', dateMode)
    this.dateInputTarget.classList.toggle('hidden', !dateMode)
    // Keep the inactive value for switching back, but exclude it from validation
    // and submission so PeriodManagement can derive it from the active field.
    this.dosesInputTarget.querySelectorAll<HTMLInputElement>('input').forEach(input => {
      input.disabled = dateMode
    })
    this.dateInputTarget.querySelectorAll<HTMLInputElement>('input').forEach(input => {
      input.disabled = !dateMode
    })
    if (dateMode) this.calculateEndDate()
  }
  
  startDateChanged(event: Event): void {
    const target = event.target as HTMLInputElement | null
    if (!target) return
    
    this.debug("Start date changed:", target.value)
    // Auto-calculate end date if date mode is selected
    if (this.isDateModeSelected()) {
      this.calculateEndDate()
    }
  }
  
  calculateEndDate(): void {
    try {
      if (!this.hasStartDateTarget || !this.hasEndDateTarget) {
        this.debug("Missing date targets")
        return
      }

      const currentEndDate = this.endDateTarget.value
      if (currentEndDate && currentEndDate !== this.suggestedEndDate) return
      
      const startDateValue = this.startDateTarget.value
      if (!startDateValue) {
        this.debug("No start date selected")
        return
      }
      
      // Date-only strings parse as UTC; use local midnight for calendar arithmetic.
      const endDate = new Date(`${startDateValue}T00:00:00`)
      if (Number.isNaN(endDate.getTime())) return
      // Suggested default only — in "date" mode the user picks the end date and
      // the server derives target_doses from it (see PeriodManagement).
      endDate.setMonth(endDate.getMonth() + 3)

      // Format date for input field (YYYY-MM-DD, local calendar day)
      const endDateString = toDateInputValue(endDate)
      this.endDateTarget.value = endDateString
      this.suggestedEndDate = endDateString
      
      this.debug("Calculated end date:", endDateString)
    } catch (error) {
      this.handleError(error, 'calculateEndDate')
    }
  }
  
  isDateModeSelected(): boolean {
    const dateRadio = this.element.querySelector<HTMLInputElement>('input[name="user[microdosing_end_type]"][value="date"]')
    return dateRadio ? dateRadio.checked : false
  }
}
